/**
 * Registro de jornada: lo que necesitan las pantallas Fichar, Mis registros y
 * Equipo, sin importar nada (como `importes.js`): probar formatos no puede
 * arrastrar la aplicación entera.
 *
 * LA REGLA DE LAS HORAS
 * ---------------------
 * El servidor guarda y devuelve las horas en UTC ("2026-07-15T06:00:00.000Z").
 * Aquí se pintan SIEMPRE en la zona de la empresa (la manda /fichajes/hoy en
 * `config.zona_horaria`), no en la del navegador: un técnico con el móvil en
 * otra zona vería horas que no son las del registro. El módulo anterior pintaba
 * UTC tal cual y una entrada a las 9:00 salía como 7:00 en verano.
 */

const DIAS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];

export function horaLocal(ts, zona) {
  if (!ts) return '-';
  return new Intl.DateTimeFormat('es-ES', {
    timeZone: zona, hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(new Date(ts));
}

/** 'YYYY-MM-DD' -> 'jue 16/07'. La fecha ya viene en hora local. */
export function fechaCorta(fecha) {
  const [a, m, d] = fecha.split('-').map(Number);
  const dia = new Date(Date.UTC(a, m - 1, d)).getUTCDay();
  return `${DIAS[dia]} ${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}`;
}

export function horasMinutos(minutos) {
  if (minutos === null || minutos === undefined) return '-';
  return `${Math.floor(minutos / 60)}:${String(minutos % 60).padStart(2, '0')}`;
}

export function duracionDesde(ts, ahora = new Date()) {
  const minutos = Math.max(0, Math.floor((ahora - new Date(ts)) / 60000));
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  return h ? `${h} h ${m} min` : `${m} min`;
}

/**
 * Qué botones tocan según el estado que devuelve /fichajes/hoy. El servidor
 * vuelve a comprobarlo todo; esto es para no ofrecer lo que va a rechazar.
 */
export function accionesPosibles({ en_jornada, en_pausa }) {
  if (!en_jornada) {
    return { principal: { tipo: 'entrada', etiqueta: 'Fichar entrada' }, secundarias: [] };
  }
  const olvido = { tipo: 'olvido_salida', etiqueta: 'Olvidé fichar la salida' };
  if (en_pausa) {
    return { principal: { tipo: 'pausa_fin', etiqueta: 'Terminar pausa' }, secundarias: [olvido] };
  }
  return {
    principal: { tipo: 'salida', etiqueta: 'Fichar salida' },
    secundarias: [{ tipo: 'pausa_inicio', etiqueta: 'Empezar pausa' }, olvido],
  };
}

function sumarDias(fecha, n) {
  const [a, m, d] = fecha.split('-').map(Number);
  return new Date(Date.UTC(a, m - 1, d + n)).toISOString().slice(0, 10);
}

/**
 * Hora de pared que se manda al corregir: 'YYYY-MM-DDTHH:MM'. Una salida a una
 * hora anterior a la de entrada es del día siguiente (turno de noche).
 */
export function horaLocalDeCorreccion(fecha, hhmm, entradaHHMM = null) {
  const dia = entradaHHMM && hhmm < entradaHHMM ? sumarDias(fecha, 1) : fecha;
  return `${dia}T${hhmm}`;
}

/** La semana (lunes a domingo) que contiene `fecha`. */
export function semanaDe(fecha) {
  const [a, m, d] = fecha.split('-').map(Number);
  const diaSemana = (new Date(Date.UTC(a, m - 1, d)).getUTCDay() + 6) % 7; // lunes = 0
  const desde = sumarDias(fecha, -diaSemana);
  const dias = Array.from({ length: 7 }, (_, i) => sumarDias(desde, i));
  return { desde, hasta: dias[6], dias };
}

export { sumarDias };

/** 'YYYY-MM-DD' de hoy en la zona de la empresa. */
export function hoyEn(zona, ahora = new Date()) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: zona }).format(ahora);
}

export function mesActual(zona, ahora = new Date()) {
  return hoyEn(zona, ahora).slice(0, 7);
}

export const NOMBRES_TIPO = {
  entrada: 'Entrada',
  salida: 'Salida',
  pausa_inicio: 'Inicio de pausa',
  pausa_fin: 'Fin de pausa',
  olvido_salida: 'Salida olvidada',
  anulacion: 'Anulación',
};

// ------------------------------------------------------------ fase 1b ---

/** Los avisos legales que calcula el servidor (fichajes_core.avisos_de_jornadas).
 *  Son para que el admin mire, no bloquean nada: el convenio puede permitirlo. */
const AVISOS = {
  sin_salida: 'sin salida',
  mas_de_9h: 'más de 9 h',
  sin_pausa: 'más de 6 h sin pausa',
  descanso_corto: 'menos de 12 h de descanso',
};

export function textoAviso(codigo) {
  return AVISOS[codigo] || codigo;
}

/** Lo que pide una solicitud, en una línea. Las horas ya son hora de pared. */
export function resumenPropuesta({ eventos = [], anular = [] }) {
  const partes = eventos.map((e) => `${NOMBRES_TIPO[e.tipo] || e.tipo} ${(e.hora_local || '').slice(11, 16)}`);
  if (anular.length) partes.push(`anular ${anular.length} fichaje${anular.length > 1 ? 's' : ''}`);
  return partes.join(' · ');
}

/** Horas extraordinarias del año frente al tope de 80 (art. 35.2 ET). */
export function topeAnual(minutos) {
  const nivel = minutos > 80 * 60 ? 'supera' : minutos >= 70 * 60 ? 'cerca' : 'normal';
  return { nivel, texto: `${horasMinutos(minutos)} de 80 h` };
}

/** Quien SOLO ficha (trabajador, representante) no tiene panel ni menú: el
 *  backend le cierra todo lo demás (fichajes_candado.py). */
export const soloFicha = (user) => user?.role === 'trabajador' || user?.role === 'labor_rep';
